import ErrorComponent from 'Components/ErrorComponent';
import ReportingFileUpload from 'Components/ReportingFileUpload';
import { errorToast, successToast } from 'Components/Toasts';
import { checkInvalidPrimaryKey, dateTimeFormat } from 'GenericForms/Helper';
import { apiMedia } from 'global/api.global';
import { RootState } from 'index';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { queryClient } from 'react-query/queryClient';
import { useSelector } from 'react-redux';
import { Button, CardBody, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { queryKeyes } from 'shared/queryKeys';
import { GetPageWithID } from '../pendingpage.hook';
import PageNavButton from '../PageNavButton';
import { VesselTypeConstant } from 'shared/constants';
import TableLoading from 'Components/TableLoading';
import Message from 'Components/Message';


const PendingCargoDocs: React.FC = () => {
    /** State variables */
    const { Vessels, VesselID } = useSelector((state: RootState) => state.Reporting);
    const [modalState, setModalState] = useState(false);
    const [fileObject, setFileObject] = useState<any>();
    const [pageUrl, setPageUrl] = useState("");
    const [file, setFile] = useState<any>([{}])
    const [update, setUpdate] = useState<boolean>(false);
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const [emptyFileError, setEmptyFileError] = useState(false);
    const [cachedReports, setCachedReports] = useState<any>(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [uploading, setUploading] = useState(false);
    /** State variables end */

    /** Queries */
    /** get pending reefer container records with pagination */
    useEffect(() => {
        if (!checkInvalidPrimaryKey(VesselID)) {
            return;
        }
        if (vessel?.vessel_type_precedence_id === VesselTypeConstant.GENERAL_CARGO_SHIP || vessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER) {
            setPageUrl(queryKeyes.vessel.PendingReeferContainer.url(VesselID));
        }
    }, [VesselID, vessel?.vessel_type_precedence_id]);
    const { data: PendingReeferContainerObj, isLoading, isError } = useQuery(
        [queryKeyes.vessel.PendingReeferContainer.key, pageUrl, VesselID],
        async () => await GetPageWithID(VesselID, pageUrl),
        {
            enabled: !!VesselID,
            onSuccess: (data) => {
                setInitialLoadComplete(true);
                // Update cached reports when new data arrives
                if (data?.results?.length > 0) {
                    setCachedReports(data);
                }
            }
        }
    );
    const displayData = PendingReeferContainerObj || cachedReports;
    const page = displayData?.results;
    /** Queries end */

    /** Open close modal */
    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }
    function tog_backdrop() {
        setModalState(!modalState);
        removeBodyCss();
    }

    /**
    * set modal true to upload file
    * set current object to setFileObject
    * @param file 
    * @param obj 
    */
    const handleUploadButtonClick = (obj: any) => {
        setModalState(!modalState);
        setFileObject(obj);
    }

    /** to upload CII Files */
    const PendingFileUploadSubmit = async () => {
        setUploading(true);
        if (fileObject && Object.keys(file[0]).length === 0) {
            setEmptyFileError(true);
        } else if (fileObject && file && file.length > 0) {
            fileObject.hasOwnProperty('log_book_upload') ?
                fileObject.log_book_upload = file[0].file :
                fileObject.baplie_file = file[0].file;
            apiMedia.patch(`/reefer_container_details/${fileObject.id}/`, fileObject)
                .then(async res => {
                    if (res.status === 200) {
                        await queryClient.invalidateQueries(queryKeyes.vessel.PendingReeferContainer.key);
                        await queryClient.invalidateQueries(queryKeyes.vessel.ReeferContainerDetailsObject.key);
                        queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByReport.key);
                        queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByVessel.key); setUpdate(!update);
                        setModalState(!modalState);
                        setFile([{}])
                        successToast("Success! Files uploaded!");
                    }
                }).catch(err => {
                    errorToast(err?.response?.data?.detail);
                }).finally(() => {
                    setUploading(false);
                })
        }
    }
    const showLoadingIndicator = !initialLoadComplete && isLoading;

    return (
        <React.Fragment>
            <Row>
                {showLoadingIndicator && <TableLoading />}
                {isError && < ErrorComponent message={'Unable to load required data!'} />}
            </Row>
            <CardBody className="p-0 pb-0 mt-2">
                <div className="table-responsive table-height-10">
                    <table className="table mb-0">
                        <thead className="table-light">
                            <tr>
                                <th className="p-2 align-middle sr-no-width">#</th>
                                <th className="p-2 align-middle" style={{ width: '22%' }}>
                                    Date & Time (Local)
                                </th>
                                <th className="p-2 align-middle">Name  of report</th>
                                <th className="p-2 align-middle d-flex justify-content-between">
                                    <span>Documents</span>
                                    <span className='mr-2'>Action</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {page && page.length > 0 ? (
                                page.flatMap((report: any, reportIndex: number) =>
                                    report.files.map((file: any, fileIndex: number) => (
                                        <tr key={`${report.id}-${fileIndex}`}>
                                            <td className="p-2 align-middle text-center">
                                                {reportIndex + 1}
                                            </td>

                                            <td className="p-2 align-middle">
                                                {dateTimeFormat(report.report_date_time, false, false, true)}
                                            </td>

                                            <td className="p-2 align-middle">
                                                {report.name_of_report}
                                            </td>

                                            <td className="p-2 align-middle">
                                                {file.hasOwnProperty("log_book_upload")
                                                    ? "Log Book"
                                                    : "Baplie File"}
                                            </td>

                                            <td className="p-2 align-middle text-center">
                                                <Button
                                                    type="button"
                                                    className="btn-sm"
                                                    color="primary"
                                                    onClick={() => handleUploadButtonClick(file)}
                                                >
                                                    Upload
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )
                            ) : (
                                <tr>
                                    <td colSpan={5} className="p-2 align-middle text-center">
                                        <Message
                                            message="No pending files to upload."
                                            type="info"
                                            width="100%"
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5} className="p-2 ">
                                    <PageNavButton
                                        setPageUrl={setPageUrl}
                                        pageobj={displayData}
                                        pageUrl={pageUrl}
                                        isLoading={isLoading}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </CardBody>
            <Modal
                size='md'
                isOpen={modalState}
                toggle={() => tog_backdrop()}
                backdrop={"static"}
                id="staticBackdrop"
            >
                <ModalHeader className='p-2'>Upload Pending Cargo Douments
                    <button
                        onClick={async () => {
                            setModalState(false);
                            setFile([{}])
                            setEmptyFileError(false);
                        }}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </ModalHeader>
                <ModalBody className='p-2'>
                    <ReportingFileUpload
                        setFile={setFile}
                        file={file}
                        index={0}
                        sm={5}
                    />
                    <div>
                        {emptyFileError &&
                            <Label className="error margin-ve-0-7">Please select a file to upload.</Label>
                        }
                    </div>
                    <div className="mt-1 mb-5">
                        <Button
                            type="submit"
                            color='primary'
                            className='pos-end'
                            disabled={uploading || !file[0].file}
                            onClick={() => {
                                PendingFileUploadSubmit()
                            }}
                        >  {uploading && <i className="spinner-border spinner-border-sm text-light me-2" />}
                            Save
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment >
    );
};

export default PendingCargoDocs;
